import React, { useState, useEffect, useCallback } from "react";
import { get, post } from "../../services/ApiService";
import toast from "react-hot-toast";
import Layout from "../../components/Layout";
import { Toaster } from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import ChatUser from "./ChatUser";
import Conversation from "./Conversation";
import ChatAction from "./ChatAction";
import { FiSearch } from "react-icons/fi";
import "./chat.css";
import { usePermissions } from "../../redux/PermissionsContext";
import { Link } from "react-router-dom";
import { useChatCounts } from "../../context/ChatCountsContext";

const CHAT_NOTIFICATIONS_EVENT = "chat_notifications_updated";

const ChatPage = () => {
  const { rolePermissions, hasPermission } = usePermissions();
  const { counts, refresh: refreshCounts } = useChatCounts();
  const [isLoading, setIsLoading] = useState(true);
  const [isMsgLoading, setIsMsgLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [type, setType] = useState("All");
  const [faqs, setFaqs] = useState([]);
  const [order, setOrder] = useState(null);
  const [awaitingReplyLoading, setAwaitingReplyLoading] = useState(false);
  const [relatedProduct, setRelatedProduct] = useState(null);
  const [ticket, setTicket] = useState(null);

  const PAGE_SIZE = 30;

  const loadPage = useCallback(
    async (pageToLoad, append) => {
      try {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        const params = new URLSearchParams();
        if (searchTerm && searchTerm.trim() !== "") params.set("search", searchTerm.trim());
        if (type && type.trim() !== "") params.set("type", type);
        if (showUnreadOnly) params.set("unreadOnly", "true");
        params.set("page", String(pageToLoad));
        params.set("limit", String(PAGE_SIZE));

        const res = await get(`api/messages/users?${params.toString()}`);
        const list = res?.customers || [];
        setUsers((prev) => (append ? [...prev, ...list] : list));
        setHasMore(Boolean(res?.hasMore));
        setPage(pageToLoad);
      } catch (error) {
        if (!append) setUsers([]);
        console.error(error.message);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [searchTerm, type, showUnreadOnly]
  );

  useEffect(() => {
    loadPage(1, false);
  }, [loadPage]);

  const handleScroll = (e) => {
    if (isLoadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 120) {
      loadPage(page + 1, true);
    }
  };
  
  const fetchFaqs = useCallback(async () => {
    try {
      const res = await get(`api/external-log/faqs`);
      if (res.success) {
        setFaqs(res.faqs || []);
      }
    } catch (error) {
      setFaqs([]);
      console.error(error.message);
    }
  }, []);

  const getMessages = useCallback(async () => {
    try {
      setIsMsgLoading(true);
      const res = await get(`api/messages/user/${selectedUser?.customerId}`);
      setIsMsgLoading(false);
      if (res.success) {
        setMessages(res.data);
        setOrder(res.order || null);
        setTicket(res.ticket);
        setRelatedProduct(res.product || null);
      } else {
        setMessages([]);
      }
    } catch (error) {
      setIsMsgLoading(false);
      setMessages([]);
      console.log(error);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser) {
      fetchFaqs();
      getMessages();
    }
  }, [selectedUser, fetchFaqs, getMessages]);

  useEffect(() => {
    const handleNotificationRefresh = () => {
      loadPage(1, false);
      refreshCounts();
    };

    window.addEventListener(CHAT_NOTIFICATIONS_EVENT, handleNotificationRefresh);
    return () =>
      window.removeEventListener(
        CHAT_NOTIFICATIONS_EVENT,
        handleNotificationRefresh
      );
  }, [loadPage, refreshCounts]);

const formatTime = (timestamp) => {
  const date = new Date(timestamp);

  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return `${day} ${month} ${year} ${time}`;
};


  const handleType = (type) => {
      setSelectedUser(null);
      setType(type); 
      setSearchTerm(""); // Clear search term when changing type
      setMessages([]); // Clear messages when changing type
  };
  const handleCloseTicket = (ticket) => {
    setTicket(ticket);
    refreshCounts();
  }

  const showAwaitingReply =
    hasPermission(["order.send_message"]) && order?.status === "PENDING";

  const handleAwaitingReply = async () => {
    if (!order?._id || awaitingReplyLoading) return;
    const next = !order.awaitingReply;
    setAwaitingReplyLoading(true);
    try {
      const res = await post(`api/orders/${order._id}/awaiting-reply`, {
        awaitingReply: next,
      });
      if (res?.success) {
        setOrder((prev) => ({
          ...prev,
          awaitingReply: res.awaitingReply,
          awaitingReplyAt: res.awaitingReplyAt,
        }));
        toast.success(next ? "Marked as awaiting reply" : "Awaiting reply cleared");
      } else {
        toast.error(res?.error || "Failed to update awaiting reply");
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update awaiting reply");
    } finally {
      setAwaitingReplyLoading(false);
    }
  };

  return (
    <Layout>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="row mb-3 align-items-center justify-content-between">
        <div className="col-sm-6 mb-2 mb-sm-0">
          <h2 className="fs-4 mb-0">Chat</h2>
          <Breadcrumbs />
        </div>
      </div>

      <div className="row g-0">
        <div className="col-md-2">
          <div className="chat-sidebar h-100">
            <p className={`${type === "All" ? "selected":""} `} onClick={() => handleType('All')}>All</p>
            <p className={`ticket-tab ${type === "OpenTicket" ? "selected" : ""}`} onClick={() => handleType("OpenTicket")}>
              <span className="tab-name">All Open Tickets</span>
              <span className="unread-count">{counts?.OpenTicket}</span>
            </p>
            <p className={`ticket-tab ${type === "OpenGsl" ? "selected" : ""}`} onClick={() => handleType("OpenGsl")}>
              <span className="tab-name">Open GSL</span>
              <span className="unread-count">{counts?.OpenGsl}</span>
            </p>
            <p className={`ticket-tab ${type === "OpenPendingPmed" ? "selected" : ""}`} onClick={() => handleType("OpenPendingPmed")}>
              <span className="tab-name">Open Pending P</span>
              <span className="unread-count">{counts?.OpenPendingPmed}</span>
            </p>
            <p className={`ticket-tab ${type === "OpenDispatchedPmed" ? "selected" : ""}`} onClick={() => handleType("OpenDispatchedPmed")}>
              <span className="tab-name">Open Complete P</span>
              <span className="unread-count">{counts?.OpenDispatchedPmed}</span>
            </p>
            <p className={`ticket-tab ${type === "NoneBuyers" ? "selected" : ""}`} onClick={() => handleType("NoneBuyers")}>
              <span className="tab-name">None Buyers</span>
              <span className="unread-count">{counts?.NoneBuyers}</span>
            </p>
          </div>
        </div>
        <div className="col-md-10">
          <div className="chat-container">
            {/* Users list sidebar */}
            <div className="chat-users-sidebar">
              <div className="chat-search">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="unread-toggle-container d-flex align-items-center justify-content-between">
                <label className="form-check-label unread-toggle-label" htmlFor="unreadSwitch">
                  Show Unread Only
                </label>
                <div className="form-check form-switch m-0 p-0 d-flex align-items-center">
                  <input
                    className="form-check-input m-0"
                    type="checkbox"
                    role="switch"
                    id="unreadSwitch"
                    checked={showUnreadOnly}
                    onChange={(e) => setShowUnreadOnly(e.target.checked)}
                  />
                </div>
              </div>
              <div className="users-list-container" onScroll={handleScroll}>
                  <ChatUser
                    isLoading={isLoading}
                    users={users}
                    setSelectedUser={setSelectedUser}
                    selectedUser={selectedUser}
                  />
                  {isLoadingMore && (
                    <p className="text-center my-2" style={{ fontSize: "12px", color: "#777" }}>
                      Loading more…
                    </p>
                  )}
                  {!isLoading && !isLoadingMore && !hasMore && users.length > 0 && (
                    <p className="text-center my-2" style={{ fontSize: "11px", color: "#aaa" }}>
                      No more conversations
                    </p>
                  )}
              </div>
            </div>

            {/* Chat area */}
            <div className="chat-area">
              {selectedUser ? (
                <>
                  <div className="chat-header">
                    <div className="chat-header-top">
                      <div className="user-info">
                        <h4>
                          {selectedUser?.name}
                          <span style={{ fontSize: '11px', fontWeight: '300' }}>
                            ({selectedUser?.customerId})
                          </span>
                        </h4>
                      </div>
                      <div className="chat-header-side">
                        <div className="chat-actions">
                          {order && (
                            <p>
                              Order: <Link to={`/orders/${order._id}`}>{order?.orderId}</Link>
                            </p>
                          )}
                          {ticket && <p>Ticket: {ticket?.ticketNo}</p>}
                        </div>
                        {showAwaitingReply && (
                          <button
                            type="button"
                            className="btn btn-sm chat-awaiting-reply"
                            onClick={handleAwaitingReply}
                            disabled={awaitingReplyLoading}
                          >
                            {awaitingReplyLoading
                              ? "Please wait..."
                              : order.awaitingReply
                                ? "Unmark awaiting reply"
                                : "Mark awaiting reply"}
                          </button>
                        )}
                      </div>
                    </div>

                    {relatedProduct && (
                      <div className="chat-header-bottom">
                        <p className="product-title">{relatedProduct.title}</p>
                        {relatedProduct?.image && (
                          <img
                            src={relatedProduct.image}
                            alt="product"
                            className="product-image"
                          />
                        )}
                      </div>
                    )}

                  </div>

                  <div className="messages-container-wrapper">
                      <Conversation 
                        isMsgLoading={isMsgLoading}
                        messages={messages} 
                        formatTime={formatTime} 
                        selectedUser={selectedUser} 
                      />
                  </div>
                   {hasPermission(['order.send_message']) &&
                  <ChatAction 
                    messages={messages}
                    selectedUser={selectedUser}
                    getMessages={getMessages}
                    faqs={faqs}
                    setFaqs={setFaqs}
                    order={order}
                    ticket={ticket}
                    handleCloseTicket={handleCloseTicket}
                  />
                   }
                </>
              ) : (
                <div className="no-chat-selected">
                  <p>Select a user to start chatting</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ChatPage;
